import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { ShoppingCart, Menu, X, Search, Phone, Mail, User, LogOut, Package } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useSiteSettings } from '../context/SiteSettingsContext';
import { Button } from './ui/button';
import SearchComponent from './SearchComponent';
import { productTypesAPI } from '../utils/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Fallback messages if API fails
const FALLBACK_MESSAGES = [
  { text: "FREE SHIPPING ON SELECT PRODUCTS", emphasis: "bold" },
  { text: "SERVING SINCE 1991", emphasis: "bold" },
  { text: "FREE ENGRAVING", emphasis: "bold" },
  { text: "FREE LOGO BASED ON QTY", emphasis: "bold" }
];

// Premium Rotating Slider Component - Right to Left with API Integration
const PromoSlider = () => {
  const [messages, setMessages] = useState(FALLBACK_MESSAGES);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  // Fetch active messages from API
  useEffect(() => {
    const fetchMessages = async () => {
      try {
        const res = await fetch(`${API_URL}/api/hero-slider/active`);
        if (res.ok) {
          const data = await res.json();
          if (data.messages && data.messages.length > 0) {
            setMessages(data.messages);
          }
        }
      } catch (error) {
        // Use fallback messages silently
        console.log('Using fallback promo messages');
      }
    };
    fetchMessages();
  }, []);

  const nextSlide = useCallback(() => {
    if (isPaused || messages.length === 0) return;
    setIsAnimating(true);
    setTimeout(() => {
      setCurrentIndex((prev) => (prev + 1) % messages.length);
      setIsAnimating(false);
    }, 400);
  }, [isPaused, messages.length]);

  useEffect(() => {
    if (messages.length <= 1) return;
    const interval = setInterval(nextSlide, 4000);
    return () => clearInterval(interval);
  }, [nextSlide, messages.length]);

  const currentMessage = messages[currentIndex];
  const emphasisClass = currentMessage?.emphasis === 'extra-bold' 
    ? 'font-black' 
    : currentMessage?.emphasis === 'bold' 
      ? 'font-bold' 
      : 'font-semibold';

  return (
    <div 
      className="relative overflow-hidden h-6 flex items-center justify-center min-w-[320px] sm:min-w-[400px] max-w-[600px]"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      data-testid="promo-slider"
    >
      <div 
        className={`absolute inset-0 flex items-center justify-center transition-all duration-400 ease-in-out ${
          isAnimating 
            ? 'opacity-0 transform translate-x-[-100%]' 
            : 'opacity-100 transform translate-x-0'
        }`}
        style={{ transitionDuration: '400ms' }}
      >
        {currentMessage?.link_url ? (
          <a 
            href={currentMessage.link_url}
            target={currentMessage.link_new_tab ? "_blank" : "_self"}
            rel="noopener noreferrer"
            className={`${emphasisClass} font-body text-sm sm:text-base tracking-wide text-white whitespace-nowrap hover:underline`}
            style={{
              textShadow: '0 0 10px rgba(255,255,255,0.3), 0 1px 2px rgba(0,0,0,0.2)'
            }}
          >
            {currentMessage?.text}
          </a>
        ) : (
          <span 
            className={`${emphasisClass} font-body text-sm sm:text-base tracking-wide text-white whitespace-nowrap`}
            style={{
              textShadow: '0 0 10px rgba(255,255,255,0.3), 0 1px 2px rgba(0,0,0,0.2)'
            }}
          >
            {currentMessage?.text}
          </span>
        )}
      </div>
    </div>
  );
};

const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [productTypes, setProductTypes] = useState([]);
  const { cartCount } = useCart();
  const { user, isAuthenticated, isAdmin, logout } = useAuth();
  const { getLogoUrl, hasCustomLogo } = useSiteSettings();
  const navigate = useNavigate();
  const location = useLocation();

  // Load product types from API for dynamic navigation
  useEffect(() => {
    const loadProductTypes = async () => {
      try {
        const response = await productTypesAPI.getProductTypes({ is_active: true });
        setProductTypes(response.product_types || []);
      } catch (err) {
        console.error('Failed to load product types for nav:', err);
        // Fallback to hardcoded
        setProductTypes([
          { slug: 'awards', ui: { label_plural: 'Awards', default_route: '/Awards' } },
          { slug: 'swag', ui: { label_plural: 'Promo Swag', default_route: '/Swag' } },
          { slug: 'large_format', ui: { label_plural: 'Large Format', default_route: '/LargePrint' } },
        ]);
      }
    };
    loadProductTypes();
  }, []);

  const handleLogout = async () => {
    await logout();
    setUserMenuOpen(false);
    navigate('/');
  };

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Build nav links - Use V2 product routes for new architecture
  const navLinks = [
    { href: '/', label: 'Home' },
    { href: '/v2/products/lgf', label: 'Large Format', icon: '🖼️' },
    { href: '/v2/products/smf', label: 'Small Format', icon: '📄' },
    { href: '/v2/products/awd', label: 'Awards', icon: '🏆' },
    { href: '/v2/products', label: 'All Products', icon: '🛍️' },
    { href: '/promotions', label: 'Deals' }
  ];

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.toLowerCase().startsWith(path.toLowerCase());
  };

  return (
    <>
      {/* Top bar - Elegant champagne gold accent with rotating promo slider */}
      <div className="bg-gradient-to-r from-gold-700 via-gold-600 to-gold-500 text-white py-2.5 overflow-hidden">
        <div className="container mx-auto px-4 flex justify-between items-center text-sm font-body">
          {/* Static Contact Info - Always Visible */}
          <div className="flex items-center gap-4 sm:gap-6 flex-shrink-0">
            <a href="tel:+18005210527" className="flex items-center gap-2 hover:text-gold-200 transition-colors" data-testid="phone-link">
              <Phone className="w-4 h-4" />
              <span className="hidden sm:inline font-medium">+1 800 521 0527</span>
            </a>
            <a href="mailto:support@engraveca.com" className="flex items-center gap-2 hover:text-gold-200 transition-colors" data-testid="email-link">
              <Mail className="w-4 h-4" />
              <span className="hidden md:inline">support@engraveca.com</span>
            </a>
          </div>
          
          {/* Rotating Promo Slider - Bold & Shiny */}
          <div className="flex-1 flex justify-center px-2">
            <PromoSlider />
          </div>
          
          {/* Spacer for balance */}
          <div className="hidden sm:block w-[180px] flex-shrink-0" />
        </div>
      </div>

      {/* Main navbar - Light, refined, elegant */}
      <nav className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled 
          ? 'bg-ivory-50/95 backdrop-blur-xl shadow-luxury border-b border-ivory-300/50' 
          : 'bg-ivory-50/90 backdrop-blur-md'
      }`}>
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-3 group" data-testid="navbar-logo">
              {hasCustomLogo ? (
                <img 
                  src={getLogoUrl()} 
                  alt="Engraveca" 
                  className="h-12 max-w-[200px] object-contain"
                  style={{ background: 'transparent' }}
                />
              ) : (
                <>
                  <div className="w-11 h-11 bg-gradient-to-br from-gold-600 to-gold-500 rounded-lg flex items-center justify-center shadow-luxury group-hover:shadow-luxury-lg transition-shadow">
                    <span className="text-white font-heading font-bold text-xl">E</span>
                  </div>
                  <span className="text-2xl font-heading font-semibold text-ivory-800">
                    Engraveca
                  </span>
                </>
              )}
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-1">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`px-4 py-2 rounded-md text-sm font-medium font-body transition-all duration-200 ${
                    isActive(link.href)
                      ? 'text-gold-700 bg-gold-500/10'
                      : 'text-ivory-600 hover:text-ivory-800 hover:bg-ivory-200/50'
                  }`}
                  data-testid={`nav-link-${link.label.toLowerCase().replace(' ', '-')}`}
                >
                  {link.label}
                </Link>
              ))}
            </div>

            {/* Right side actions */}
            <div className="flex items-center gap-2">
              {/* Search Button */}
              <Button
                variant="ghost"
                className="hidden md:flex items-center gap-2 text-ivory-500 hover:text-ivory-800 hover:bg-ivory-200/50 rounded-lg px-3 font-body"
                onClick={() => setSearchOpen(true)}
                data-testid="search-button"
              >
                <Search className="w-5 h-5" />
                <span className="text-sm hidden lg:inline">Search</span>
                <kbd className="hidden lg:inline-flex items-center gap-1 px-2 py-0.5 text-xs font-mono bg-ivory-200 text-ivory-500 rounded border border-ivory-300">
                  ⌘K
                </kbd>
              </Button>

              <Button
                variant="ghost"
                size="icon"
                className="md:hidden text-ivory-500 hover:text-ivory-800 hover:bg-ivory-200/50 rounded-lg"
                onClick={() => setSearchOpen(true)}
                data-testid="search-button-mobile"
              >
                <Search className="w-5 h-5" />
              </Button>

              {/* User Account / Login */}
              {isAuthenticated ? (
                <div className="relative">
                  <Button
                    variant="ghost"
                    className="flex items-center gap-2 text-ivory-600 hover:text-ivory-800 hover:bg-ivory-200/50 rounded-lg px-3 font-body"
                    onClick={() => setUserMenuOpen(!userMenuOpen)}
                    data-testid="user-menu-button"
                  >
                    <div className="w-8 h-8 bg-gradient-to-br from-gold-600 to-gold-500 rounded-full flex items-center justify-center">
                      <span className="text-white font-bold text-sm">
                        {user?.name?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || 'U'}
                      </span>
                    </div>
                    <span className="hidden lg:inline text-sm font-medium truncate max-w-[120px]">
                      {user?.name || user?.email?.split('@')[0]}
                    </span>
                  </Button>
                  
                  {/* User Dropdown Menu */}
                  {userMenuOpen && (
                    <div className="absolute right-0 top-full mt-2 w-56 bg-ivory-50 rounded-xl border border-ivory-300/50 shadow-luxury py-2 z-50">
                      <div className="px-4 py-2 border-b border-ivory-200">
                        <p className="text-sm font-medium text-ivory-800 font-body truncate">{user?.name || 'User'}</p>
                        <p className="text-xs text-ivory-500 font-body truncate">{user?.email}</p>
                      </div>
                      
                      {isAdmin && (
                        <Link
                          to="/admin"
                          className="flex items-center gap-2 px-4 py-2 text-sm text-ivory-700 hover:bg-gold-500/10 hover:text-gold-700 font-body"
                          onClick={() => setUserMenuOpen(false)}
                          data-testid="admin-dashboard-link"
                        >
                          <User className="w-4 h-4" />
                          Admin Dashboard
                        </Link>
                      )}
                      
                      <Link
                        to="/my-orders"
                        className="flex items-center gap-2 px-4 py-2 text-sm text-ivory-700 hover:bg-gold-500/10 hover:text-gold-700 font-body"
                        onClick={() => setUserMenuOpen(false)}
                        data-testid="my-orders-link"
                      >
                        <Package className="w-4 h-4" />
                        My Orders
                      </Link>
                      
                      <Link
                        to="/my-account"
                        className="flex items-center gap-2 px-4 py-2 text-sm text-ivory-700 hover:bg-gold-500/10 hover:text-gold-700 font-body"
                        onClick={() => setUserMenuOpen(false)}
                        data-testid="my-account-link"
                      >
                        <User className="w-4 h-4" />
                        My Account
                      </Link>
                      
                      <Link
                        to="/track-order"
                        className="flex items-center gap-2 px-4 py-2 text-sm text-ivory-700 hover:bg-ivory-200/50 font-body"
                        onClick={() => setUserMenuOpen(false)}
                        data-testid="track-order-link"
                      >
                        <ShoppingCart className="w-4 h-4" />
                        Track Order
                      </Link>
                      
                      <button
                        onClick={handleLogout}
                        className="flex items-center gap-2 w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 font-body"
                        data-testid="logout-button"
                      >
                        <LogOut className="w-4 h-4" />
                        Sign Out
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="hidden sm:flex items-center gap-2">
                  <Link to="/login">
                    <Button
                      variant="ghost"
                      className="text-ivory-600 hover:text-ivory-800 hover:bg-ivory-200/50 rounded-lg font-body text-sm"
                      data-testid="login-nav-button"
                    >
                      Sign In
                    </Button>
                  </Link>
                  <Link to="/register">
                    <Button
                      className="btn-luxury-primary rounded-lg font-body text-sm px-4"
                      data-testid="register-nav-button"
                    >
                      Sign Up
                    </Button>
                  </Link>
                </div>
              )}

              <Button
                variant="ghost"
                size="icon"
                className="relative text-ivory-500 hover:text-ivory-800 hover:bg-ivory-200/50 rounded-lg"
                onClick={() => navigate('/cart')}
                data-testid="cart-button"
              >
                <ShoppingCart className="w-5 h-5" />
                {cartCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-gradient-to-r from-gold-600 to-gold-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold shadow-sm">
                    {cartCount}
                  </span>
                )}
              </Button>

              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden text-ivory-500 hover:text-ivory-800 hover:bg-ivory-200/50 rounded-lg"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                data-testid="mobile-menu-button"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </Button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden border-t border-ivory-300/50 py-4 px-4 bg-ivory-50/95 backdrop-blur-xl">
            <div className="flex flex-col space-y-1">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`px-4 py-3 rounded-lg font-medium font-body transition-all ${
                    isActive(link.href)
                      ? 'text-gold-700 bg-gold-500/10'
                      : 'text-ivory-600 hover:text-ivory-800 hover:bg-ivory-200/50'
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(' ', '-')}`}
                >
                  {link.label}
                </Link>
              ))}
              
              {/* Mobile Auth Links */}
              <div className="border-t border-ivory-200 mt-2 pt-2">
                {isAuthenticated ? (
                  <>
                    <div className="px-4 py-2">
                      <p className="text-sm font-medium text-ivory-800 font-body">{user?.name || user?.email}</p>
                      <p className="text-xs text-ivory-500 font-body">{user?.email}</p>
                    </div>
                    {isAdmin && (
                      <Link
                        to="/admin"
                        className="flex items-center gap-2 px-4 py-3 text-gold-700 bg-gold-500/10 rounded-lg font-medium font-body"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        <User className="w-4 h-4" />
                        Admin Dashboard
                      </Link>
                    )}
                    <Link
                      to="/my-orders"
                      className="flex items-center gap-2 px-4 py-3 text-gold-700 bg-gold-500/10 rounded-lg font-medium font-body"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <Package className="w-4 h-4" />
                      My Orders
                    </Link>
                    <Link
                      to="/track-order"
                      className="flex items-center gap-2 px-4 py-3 text-ivory-600 hover:text-ivory-800 hover:bg-ivory-200/50 rounded-lg font-body"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <ShoppingCart className="w-4 h-4" />
                      Track Order
                    </Link>
                    <button
                      onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                      className="flex items-center gap-2 w-full px-4 py-3 text-red-600 hover:bg-red-50 rounded-lg font-body"
                    >
                      <LogOut className="w-4 h-4" />
                      Sign Out
                    </button>
                  </>
                ) : (
                  <>
                    <Link
                      to="/login"
                      className="flex items-center gap-2 px-4 py-3 text-ivory-600 hover:text-ivory-800 hover:bg-ivory-200/50 rounded-lg font-body"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <User className="w-4 h-4" />
                      Sign In
                    </Link>
                    <Link
                      to="/register"
                      className="flex items-center gap-2 px-4 py-3 text-gold-700 bg-gold-500/10 rounded-lg font-medium font-body"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Sign Up
                    </Link>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Search Modal */}
      <SearchComponent isOpen={searchOpen} onClose={() => setSearchOpen(false)} />
    </>
  );
};

export default Navbar;
